import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, getAuthHeaders, apiRequest } from '../utils/api';

const CronJobManager = () => {
  const { user } = useAuth();
  const [jobStatus, setJobStatus] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showScheduleConfig, setShowScheduleConfig] = useState(false);
  const [scheduleConfig, setScheduleConfig] = useState({
    interval_type: 'weeks',
    interval_value: 1,
    duration_type: 'weeks',
    duration_value: 5
  });

  useEffect(() => {
    fetchJobStatus();
    fetchJobHistory();
  }, []);

  // Update schedule config when job status changes
  useEffect(() => {
    if (jobStatus && jobStatus.schedule_config) {
      setScheduleConfig(jobStatus.schedule_config);
    }
  }, [jobStatus]);

  const fetchJobStatus = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/admin/cron-jobs/status');
      const data = await response.json();
      if (data.success) {
        setJobStatus(data.status);
      } else {
        setMessage('Error fetching job status: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      setMessage('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobHistory = async () => {
    try {
      const response = await apiRequest('/admin/cron-jobs/history');
      const data = await response.json();
      if (data.success) {
        setJobHistory(data.history || []);
      } else {
        console.error('Error fetching job history:', data.error || 'Unknown error');
        setJobHistory([]);
      }
    } catch (err) {
      console.error('Error fetching job history:', err);
      setJobHistory([]);
    }
  };

  const handleStartJobs = async () => {
    try {
      setActionLoading(true);
      const response = await apiRequest('/admin/cron-jobs/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scheduleConfig),
      });

      const data = await response.json();
      if (data.success) {
        setMessage('Cron jobs started successfully!');
        fetchJobStatus();
        setShowScheduleConfig(false);
      } else {
        setMessage('Error starting cron jobs: ' + (data.error || data.message || 'Unknown error'));
      }
    } catch (err) {
      setMessage('Network error: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStopJobs = async () => {
    try {
      setActionLoading(true);
      const response = await apiRequest('/admin/cron-jobs/stop', {
        method: 'POST',
      });

      const data = await response.json();
      if (data.success) {
        setMessage('Cron jobs stopped successfully!');
        fetchJobStatus();
      } else {
        setMessage('Error stopping cron jobs: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      setMessage('Network error: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRunManualUpdate = async () => {
    try {
      setActionLoading(true);
      const response = await apiRequest('/admin/cron-jobs/run-manual', {
        method: 'POST',
      });

      const data = await response.json();
      if (data.success) {
        setMessage('Manual update completed successfully!');
        fetchJobStatus();
        fetchJobHistory();
      } else {
        setMessage('Error running manual update: ' + (data.error || data.message || 'Unknown error'));
      }
    } catch (err) {
      setMessage('Network error: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateScheduleConfig = async () => {
    try {
      setActionLoading(true);
      const response = await apiRequest('/admin/cron-jobs/schedule-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scheduleConfig),
      });

      const data = await response.json();
      if (data.success) {
        setMessage('Schedule configuration updated successfully!');
        fetchJobStatus();
        setShowScheduleConfig(false);
      } else {
        setMessage('Error updating schedule config: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      setMessage('Network error: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (isRunning) => {
    return isRunning ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  const getSuccessRateColor = (rate) => {
    if (rate >= 90) return 'text-green-600';
    if (rate >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }


  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Cron Job Manager</h1>
            <p className="text-gray-600">Manage automated campaign metrics updates</p>
          </div>
        </div>
      </div>

      {message && (
        <div className="mb-6 p-4 rounded-md bg-blue-50 border border-blue-200">
          <p className="text-blue-800">{message}</p>
        </div>
      )}

      {/* Job Status Card */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Current Status</h2>
        </div>
        <div className="px-6 py-4">
          {jobStatus ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getStatusColor(jobStatus.is_running)}`}>
                    {jobStatus.is_running ? 'Running' : 'Stopped'}
                  </span>
                </div>
                <div className="text-sm text-gray-500">Scheduler Status</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  {jobStatus.weeks_elapsed ? Math.round(jobStatus.weeks_elapsed * 10) / 10 : 0}
                </div>
                <div className="text-sm text-gray-500">Weeks Elapsed</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 mb-1">
                  {jobStatus.schedule_config ? 
                    `${jobStatus.schedule_config.duration_value} ${jobStatus.schedule_config.duration_type}` :
                    jobStatus.max_weeks + ' weeks'
                  }
                </div>
                <div className="text-sm text-gray-500">Duration</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600 mb-1">
                  {jobStatus.should_continue ? 'Yes' : 'No'}
                </div>
                <div className="text-sm text-gray-500">Will Continue</div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500">No status information available</div>
          )}
        </div>
      </div>

      {/* Schedule Configuration */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Schedule Configuration</h2>
            <button
              onClick={() => setShowScheduleConfig(!showScheduleConfig)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {showScheduleConfig ? 'Hide Config' : 'Configure Schedule'}
            </button>
          </div>
        </div>
        <div className="px-6 py-4">
          {jobStatus && jobStatus.schedule_config ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Current Schedule</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Interval:</span>
                    <span className="text-sm font-medium text-gray-900">
                      Every {jobStatus.schedule_config.interval_value} {jobStatus.schedule_config.interval_type}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Duration:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {jobStatus.schedule_config.duration_value} {jobStatus.schedule_config.duration_type}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Next Execution</h3>
                <div className="text-sm text-gray-900">
                  {jobStatus.is_running ? 
                    `Runs every ${jobStatus.schedule_config.interval_value} ${jobStatus.schedule_config.interval_type}` :
                    'Scheduler is stopped'
                  }
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500">
              <p>No schedule configuration available</p>
            </div>
          )}
        </div>
      </div>

      {/* Schedule Configuration Form */}
      {showScheduleConfig && (
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Configure Schedule</h2>
            <p className="text-sm text-gray-500 mt-1">Set how often and for how long the cron jobs should run</p>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Run Every</label>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    min="1"
                    value={scheduleConfig.interval_value}
                    onChange={(e) => setScheduleConfig({
                      ...scheduleConfig,
                      interval_value: parseInt(e.target.value) || 1
                    })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <select
                    value={scheduleConfig.interval_type}
                    onChange={(e) => setScheduleConfig({
                      ...scheduleConfig,
                      interval_type: e.target.value
                    })}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Run For</label>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    min="1"
                    value={scheduleConfig.duration_value}
                    onChange={(e) => setScheduleConfig({
                      ...scheduleConfig,
                      duration_value: parseInt(e.target.value) || 1
                    })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <select
                    value={scheduleConfig.duration_type}
                    onChange={(e) => setScheduleConfig({
                      ...scheduleConfig,
                      duration_type: e.target.value
                    })}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowScheduleConfig(false)}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateScheduleConfig}
                disabled={actionLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : null}
                Update Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Control Panel */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Control Panel</h2>
        </div>
        <div className="px-6 py-4">
          <div className="flex flex-wrap gap-4">
            <button
              onClick={handleStartJobs}
              disabled={actionLoading || (jobStatus && jobStatus.is_running)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actionLoading ? (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m6-6a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              Start Scheduler
            </button>

            <button
              onClick={handleStopJobs}
              disabled={actionLoading || (jobStatus && !jobStatus.is_running)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actionLoading ? (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10h6v4H9z" />
                </svg>
              )}
              Stop Scheduler
            </button>

            <button
              onClick={handleRunManualUpdate}
              disabled={actionLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actionLoading ? (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
              Run Manual Update
            </button>

            <button
              onClick={() => {
                fetchJobStatus();
                fetchJobHistory();
                setMessage('');
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Job History */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Execution History</h2>
        </div>
        <div className="overflow-x-auto">
          {jobHistory.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Campaigns
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Failed
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Success Rate
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobHistory.map((job, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(job.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {formatDuration(job.duration_seconds)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {job.total_campaigns}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 text-center font-medium">
                      {job.updated_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 text-center font-medium">
                      {job.failed_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      <span className={`font-medium ${getSuccessRateColor(job.success_rate)}`}>
                        {job.success_rate.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-6 py-8 text-center text-gray-500">
              No job execution history available
            </div>
          )}
        </div>
      </div>

      {/* Information Panel */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-3">How it works</h3>
        <div className="text-blue-800 space-y-2">
          <p>• <strong>Flexible Scheduling:</strong> Configure how often and for how long the system should run (days, weeks, or months)</p>
          <p>• <strong>AI Extraction:</strong> Uses Gemini AI to analyze post URLs and extract current metrics</p>
          <p>• <strong>Custom Duration:</strong> Set your own duration limit to prevent indefinite running</p>
          <p>• <strong>Manual Control:</strong> Admins can start, stop, or trigger manual updates anytime</p>
          <p>• <strong>Real-time Analytics:</strong> All TCS scores, ER%, CPV, CPE calculations are updated automatically</p>
          <p>• <strong>Smart Configuration:</strong> Update schedule settings even while the scheduler is running</p>
        </div>
      </div>
    </div>
  );
};

export default CronJobManager;
